import { Button, Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, Separator, cn } from "@tellescope/ui";
import { Menu, LogOut, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ChildPage, Page } from "../../shared/schema";

interface PortalNavbarProps {
  pages: Page[];
  currentPage: Page | ChildPage | null;
  onNavigate: (pageId: string) => void;
  portalName?: string;
  onLogout?: () => void;
  /** Organization logo URL - displayed instead of text when available */
  logoURL?: string;
  /** Organization name - used as fallback when no logo is available */
  organizationName?: string;
  /**
   * When true, renders navigation items as <Link> components for native link behavior
   * (right-click to open in new tab, browser history support, etc.)
   * When false, renders as buttons that call onNavigate callback.
   * Default: false (for admin preview compatibility)
   */
  useLinks?: boolean;
  /** The slug of the first page in the full schema (used to resolve the "/" URL correctly when pages may be filtered) */
  rootSlug?: string;
}

export const PortalNavbar: React.FC<PortalNavbarProps> = ({
  pages,
  currentPage,
  onNavigate,
  portalName,
  onLogout,
  logoURL,
  organizationName,
  useLinks = false,
  rootSlug,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Tracks which single top-level page's sub-pages are expanded (collapsed by default,
  // except the parent of the initially-active sub-page so a deep link lands expanded).
  const [expandedPageId, setExpandedPageId] = useState<string | null>(() => {
    const activeParent = pages.find((p) => p.pages.some((c) => c.id === currentPage?.id));
    return activeParent?.id ?? null;
  });

  // Use rootSlug to correctly resolve "/" URL even when pages may be filtered by visibility
  const firstPageSlug = rootSlug ?? pages[0]?.slug;

  const handleNavigate = (pageId: string) => {
    onNavigate(pageId);
    setOpen(false);
  };

  // Get the URL path for a top-level page
  const getPagePath = (page: Page): string => {
    return page.slug === firstPageSlug ? "/" : `/${page.slug}`;
  };

  // Get the URL path for a sub-page nested under a top-level page
  const getChildPagePath = (page: Page, child: ChildPage): string => {
    const parentPath = getPagePath(page);
    return `${parentPath === "/" ? "" : parentPath}/${child.slug}`;
  };

  const handleLogout = () => {
    setOpen(false);
    onLogout?.();
  };

  // Display name: use organization name if available, fall back to portal name, then default
  const displayName = organizationName || portalName || t("My Portal");

  return (
    <nav
      className="flex items-center justify-between px-4 py-3 border-b bg-background"
      style={{ backgroundColor: "var(--portal-header-color, var(--background))" }}
    >
      {/* Left side: Hamburger menu + Logo/Name + Current page */}
      <div className="flex items-center gap-3">
        {/* Hamburger menu - moved to left side */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <Menu className="h-5 w-5" />
              <span className="sr-only">{t("Open menu")}</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 px-4" hideOverlay>
            <SheetHeader className="px-2">
              <SheetTitle>{t("Pages")}</SheetTitle>
            </SheetHeader>
            <nav className="mt-6 space-y-2">
              {pages.map((page) => {
                const isActive = currentPage?.id === page.id;
                const hasChildren = page.pages.length > 0;
                const isExpanded = expandedPageId === page.id;
                const navItemClasses = cn(
                  "flex-1 text-left px-4 py-3 rounded-md text-sm transition-colors block",
                  isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                );

                const expandOnNavigate = () => {
                  if (hasChildren) setExpandedPageId(page.id);
                };

                return (
                  <div key={page.id}>
                    <div className="flex items-center gap-1">
                      {useLinks ? (
                        <Link
                          to={getPagePath(page)}
                          onClick={() => {
                            setOpen(false);
                            expandOnNavigate();
                          }}
                          className={navItemClasses}
                        >
                          {page.title}
                        </Link>
                      ) : (
                        <button
                          onClick={() => {
                            handleNavigate(page.id);
                            expandOnNavigate();
                          }}
                          className={navItemClasses}
                        >
                          {page.title}
                        </button>
                      )}

                      {hasChildren && (
                        <button
                          type="button"
                          onClick={() => setExpandedPageId(isExpanded ? null : page.id)}
                          className={cn(
                            "p-2 rounded-md hover:bg-muted flex-shrink-0",
                            isActive ? "text-primary-foreground" : "text-muted-foreground"
                          )}
                          title={isExpanded ? t("Collapse sub-pages") : t("Expand sub-pages")}
                        >
                          <ChevronDown className={cn("h-4 w-4 transition-transform", isExpanded ? "rotate-180" : "")} />
                        </button>
                      )}
                    </div>

                    {hasChildren && isExpanded && (
                      <div className="pl-4 space-y-1 mt-1">
                        {page.pages.map((child) => {
                          const childNavItemClasses = cn(
                            "w-full text-left px-4 py-2 rounded-md text-sm transition-colors block",
                            currentPage?.id === child.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                          );

                          return useLinks ? (
                            <Link
                              key={child.id}
                              to={getChildPagePath(page, child)}
                              onClick={() => setOpen(false)}
                              className={childNavItemClasses}
                            >
                              {child.title}
                            </Link>
                          ) : (
                            <button
                              key={child.id}
                              onClick={() => handleNavigate(child.id)}
                              className={childNavItemClasses}
                            >
                              {child.title}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Logout section below pages */}
            {onLogout && (
              <div className="mt-4">
                <Separator className="mb-4" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-3 rounded-md text-sm text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  {t("Log out")}
                </button>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Logo or Organization Name */}
        {logoURL ? (
          <img src={logoURL} alt={displayName} className="h-8 max-w-[150px] object-contain" />
        ) : (
          <span className="text-sm font-medium text-foreground">{displayName}</span>
        )}

        {/* Current page indicator */}
        <span className="text-muted-foreground">/</span>
        <h1 className="text-sm font-semibold">{currentPage?.title ?? t("Home")}</h1>
      </div>

      {/* Right side - empty for now, keeps layout balanced */}
      <div />
    </nav>
  );
};
